// 訂閱管理工具 - 統一管理用戶訂閱資料
export const subscriptionManager = {
  
  // 獲取用戶訂閱資料
  getUserSubscription: async () => {
    try {
      // 首先嘗試從後端 API 獲取
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await apiClient.get('/subscription');
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              // 儲存到 localStorage 作為快取
              localStorage.setItem('userSubscription', JSON.stringify(data.data));
              return data.data;
            }
          }
        } catch (apiError) {
          console.log('API call failed, using local data:', apiError);
        }
      }
      
      // 如果 API 失敗，嘗試從 localStorage 獲取
      const profileData = localStorage.getItem('userSubscription');
      const purchaseData = localStorage.getItem('currentPurchase');
      
      if (profileData) {
        // 如果有存儲的訂閱資料，直接使用
        return JSON.parse(profileData);
      } else if (purchaseData) {
        // 如果有購買資料，轉換為訂閱格式
        const parsed = JSON.parse(purchaseData);
        const subscription = {
          accountPlans: [{
            type: parsed.planType || 'basic',
            name: parsed.planName || 'AI 基礎版',
            count: parsed.accounts || 2,
            pricePerAccount: parsed.pricePerAccount || 490
          }],
          totalAccounts: parsed.accounts || 2,
          totalPrice: parsed.totalPrice || 980,
          status: 'active',
          canceledAt: null,
          nextBillingDate: '2025-10-03',
          currentPeriodEnd: '2025-10-03',
          paymentMethod: 'ECPay',
          purchaseDate: '2025-09-03',
          autoRenew: true
        };
        
        // 儲存轉換後的訂閱資料
        subscriptionManager.saveUserSubscription(subscription);
        return subscription;
      }
      
      // 默認訂閱資料
      return subscriptionManager.getDefaultSubscription();
      
    } catch (error) {
      console.error('Error loading user subscription:', error);
      return subscriptionManager.getDefaultSubscription();
    }
  },

  // 獲取默認訂閱資料
  getDefaultSubscription: () => {
    return {
      accountPlans: [
        { type: 'basic', name: 'AI 基礎版', count: 2, pricePerAccount: 490 }
      ],
      totalAccounts: 2,
      totalPrice: 980,
      status: 'active',
      canceledAt: null,
      nextBillingDate: '2025-10-03',
      currentPeriodEnd: '2025-10-03',
      paymentMethod: 'ECPay',
      purchaseDate: '2025-09-03',
      autoRenew: true
    };
  },

  // 儲存用戶訂閱資料
  saveUserSubscription: (subscription) => {
    try {
      localStorage.setItem('userSubscription', JSON.stringify(subscription));
    } catch (error) {
      console.error('Error saving user subscription:', error);
    }
  },

  // 更新訂閱資料（加購帳號等）
  updateSubscription: (updates) => {
    try {
      const currentSubscription = subscriptionManager.getUserSubscription();
      const updatedSubscription = { ...currentSubscription, ...updates };
      subscriptionManager.saveUserSubscription(updatedSubscription);
      return updatedSubscription;
    } catch (error) {
      console.error('Error updating subscription:', error);
      return null;
    }
  },

  // 加購帳號
  addAccounts: async (planType, planName, count, pricePerAccount) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('未找到認證令牌');
      }

      const response = await fetch('/api/subscription/add-accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planType,
          planName,
          count,
          pricePerAccount
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || '加購失敗');
      }

      if (data.success) {
        // 更新本地存儲
        subscriptionManager.saveUserSubscription(data.data);
        return data.data;
      } else {
        throw new Error(data.message || '加購失敗');
      }
    } catch (error) {
      console.error('Error adding accounts:', error);
      return null;
    }
  },

  // 取消訂閱
  cancelSubscription: () => {
    try {
      const subscription = subscriptionManager.getUserSubscription();
      const updatedSubscription = {
        ...subscription,
        status: 'canceled',
        canceledAt: new Date().toISOString().split('T')[0],
        autoRenew: false
      };
      
      subscriptionManager.saveUserSubscription(updatedSubscription);
      return updatedSubscription;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return null;
    }
  },

  // 重新啟用訂閱
  reactivateSubscription: () => {
    try {
      const subscription = subscriptionManager.getUserSubscription();
      const updatedSubscription = {
        ...subscription,
        status: 'active',
        canceledAt: null,
        autoRenew: true
      };
      
      subscriptionManager.saveUserSubscription(updatedSubscription);
      return updatedSubscription;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      return null;
    }
  },

  // 獲取帳號限制
  getAccountsLimit: () => {
    const subscription = subscriptionManager.getUserSubscription();
    return subscription.totalAccounts;
  },

  // 檢查是否可以添加帳號
  canAddAccount: (currentAccountCount) => {
    const limit = subscriptionManager.getAccountsLimit();
    const subscription = subscriptionManager.getUserSubscription();
    
    return subscription.status === 'active' && currentAccountCount < limit;
  },

  // 清除訂閱資料（登出時使用）
  clearSubscriptionData: () => {
    try {
      localStorage.removeItem('userSubscription');
      localStorage.removeItem('currentPurchase');
    } catch (error) {
      console.error('Error clearing subscription data:', error);
    }
  },

  // 獲取用戶的方案類型
  getUserPlanType: async () => {
    try {
      const subscription = await subscriptionManager.getUserSubscription();
      if (subscription && subscription.accountPlans && subscription.accountPlans.length > 0) {
        const planType = subscription.accountPlans[0].type;
        return planType; // 'basic', 'professional', 'brand'
      }
      return 'basic'; // 默認為基礎版
    } catch (error) {
      console.error('Error getting user plan type:', error);
      return 'basic';
    }
  },

  // 獲取方案可用的模板數量
  getAvailableTemplatesCount: async () => {
    const planType = await subscriptionManager.getUserPlanType();
    const templateLimits = {
      'basic': 1,      // AI 基礎版：1個模板
      'professional': 3, // AI 專業版：3個模板
      'brand': 0       // AI 品牌版：暫時不做
    };
    return templateLimits[planType] || 1;
  }
};

export default subscriptionManager;